import { type QuoteWizardFlow } from '@/app/config/products.config';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { useRdProposal } from '@/features/quotes/hooks/rd-equipamentos/use-rd-proposal.hook';
import type { RdProductType } from '@/services/quotes/rd-equipamentos/types';
import { isRdProposalFrozen } from '@/services/quotes/rd-equipamentos/types';
import { Alert, Button, Steps } from '@noorden-insurtech/alba-design-system';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ComponentType,
} from 'react';
import type {
  RdQuoteStepOnNextResult,
  RdQuoteStepProps,
  RdStepCode,
} from './rd-quote-step.types';
import { StepCliente } from './steps/step-cliente.component';
import { StepCotacao } from './steps/step-cotacao.component';
import { StepEmissao } from './steps/step-emissao.component';
import { StepEquipamentos } from './steps/step-equipamentos.component';
import { StepFrota } from './steps/step-frota.component';
import { StepParametros } from './steps/step-parametros.component';

/** Step "achatado" — versão leve de `QuoteStep` (sem `step`/`stepNumber`/`sections`) usada apenas para montar/adaptar a lista do stepper. */
type RdWizardStep = {
  code: RdStepCode;
  stepName: string;
};

/**
 * Etapas do wizard, fixas: o RD não tem schema dinâmico no BFF (`GET
 * /quotes/schemas` só atende as `QuoteCategory` genéricas) — este componente
 * é exclusivo do ramo 0171 e sempre montou o stepper a partir daqui.
 *
 * São os 5 códigos do fluxo individual; `FLEET` é inserido dinamicamente por
 * `adaptStepsForFlow` nos fluxos agregados. A etapa `COVERAGES` foi fundida em
 * `EQUIPMENT` (as coberturas são preenchidas no mesmo formulário do
 * equipamento) — deep-links antigos para ela caem em `LEGACY_STEP_CODES`.
 */
const RD_WIZARD_STEPS: RdWizardStep[] = [
  { code: 'CLIENT_INFO', stepName: 'Cliente' },
  { code: 'EQUIPMENT', stepName: 'Equipamentos' },
  { code: 'COMMERCIALIZATION', stepName: 'Parâmetros' },
  { code: 'CALCULATION', stepName: 'Cotação' },
  { code: 'EMISSION', stepName: 'Emissão' },
];

/** Deep-link antigo → etapa que hoje faz o trabalho dele. */
const LEGACY_STEP_CODES: Record<string, string> = {
  COVERAGES: 'EQUIPMENT',
};

/**
 * Para `frota`/`lmi_unico` o wizard não coleta equipamentos individuais
 * nem coberturas por item — troca o step de equipamentos por um único
 * `FLEET` ("Frota" ou "LMI Único", conforme o produto) logo após
 * `CLIENT_INFO`.
 */
function adaptStepsForFlow(
  steps: RdWizardStep[],
  productType: RdProductType
): RdWizardStep[] {
  if (productType === 'individual') return steps;

  const fleetStep: RdWizardStep = {
    code: 'FLEET',
    stepName: productType === 'frota' ? 'Frota' : 'LMI Único',
  };

  const withoutItemSteps = steps.filter(step => step.code !== 'EQUIPMENT');

  const clientInfoIndex = withoutItemSteps.findIndex(
    step => step.code === 'CLIENT_INFO'
  );
  if (clientInfoIndex === -1) return [fleetStep, ...withoutItemSteps];

  return [
    ...withoutItemSteps.slice(0, clientInfoIndex + 1),
    fleetStep,
    ...withoutItemSteps.slice(clientInfoIndex + 1),
  ];
}

// `Record<string, ...>` (não `Record<RdStepCode, ...>`): abaixo indexamos
// `STEP_REGISTRY[activeCode]` com uma string simples (não um literal), o
// que o TS rejeitaria (TS7053) contra um Record de chaves literais.
const STEP_REGISTRY: Record<string, ComponentType<RdQuoteStepProps>> = {
  CLIENT_INFO: StepCliente,
  EQUIPMENT: StepEquipamentos,
  FLEET: StepFrota,
  COMMERCIALIZATION: StepParametros,
  CALCULATION: StepCotacao,
  EMISSION: StepEmissao,
};

type Props = {
  productId: string;
  category: string;
  productCode: string;
  stepCode: string;
  quoteFlow?: QuoteWizardFlow;
};

export function NewRdEquipamentosForm({
  productId,
  category,
  productCode,
  stepCode,
  quoteFlow = 'new',
}: Props) {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const { id, policyId, setParams } = useQuoteParams();
  const hasNavigatedToId = useRef(false);

  useEffect(() => {
    const urlId = typeof params.id === 'string' ? params.id.trim() : '';
    if (!urlId) return;
    if (id?.trim() || policyId?.trim()) return;
    setParams({ policyId: urlId, id: urlId });
  }, [params.id, id, policyId, setParams]);

  const resolvedProposalId = (id || policyId || '').trim();
  /** Id direto da URL — disponível já no primeiro render, ao contrário do
   * contexto, que só é preenchido por um efeito posterior. */
  const proposalIdInUrl = typeof params.id === 'string' ? params.id.trim() : '';

  const {
    data: proposal,
    isLoading: isProposalLoading,
    isError: isProposalError,
  } = useRdProposal(resolvedProposalId || undefined);

  const productType: RdProductType = proposal?.productType ?? 'individual';

  const steps = useMemo(
    () => adaptStepsForFlow(RD_WIZARD_STEPS, productType),
    [productType]
  );

  // Deep-links antigos (`stepCode=COVERAGES`) precisam cair na etapa que
  // absorveu o passo; sem isso o efeito de redirect abaixo trataria o código
  // como inexistente e devolveria o corretor ao primeiro step.
  const requestedCode = LEGACY_STEP_CODES[stepCode] ?? stepCode;
  const activeCode = requestedCode || steps[0]?.code || '';
  const activeIndex = useMemo(
    () => steps.findIndex(s => s.code === activeCode),
    [steps, activeCode]
  );
  const safeIndex = Math.max(activeIndex, 0);

  const baseSearch = useMemo(
    () => ({
      productId: productId || '',
      category: category || '',
      productCode: productCode || '',
      quoteFlow,
    }),
    [productId, category, productCode, quoteFlow]
  );

  const navigateToStep = useCallback(
    (search: typeof baseSearch & { stepCode: string }, replaceNav = false) => {
      const proposalRowId =
        (typeof params.id === 'string' && params.id.trim()) ||
        id?.trim() ||
        policyId?.trim() ||
        '';
      if (proposalRowId) {
        navigate({
          to: '/quotes/products/new/$id',
          params: { id: proposalRowId },
          search,
          replace: replaceNav,
          viewTransition: false,
        }).catch(() => {});
      } else {
        navigate({
          to: '/quotes/products/new',
          search,
          replace: replaceNav,
          viewTransition: false,
        }).catch(() => {});
      }
    },
    [navigate, params.id, id, policyId]
  );

  const goToStep = useCallback(
    (nextCode: string, replaceNav = false) => {
      navigateToStep({ ...baseSearch, stepCode: nextCode }, replaceNav);
    },
    [navigateToStep, baseSearch]
  );

  useEffect(() => {
    if (!steps.length) return;
    // Enquanto a proposta carrega, `productType` ainda é o default
    // 'individual' e `steps` não contém `FLEET`. Redirecionar aqui fazia
    // qualquer deep-link `stepCode=FLEET` ser reescrito para `CLIENT_INFO`
    // antes de descobrirmos que a proposta é frota/LMI único — o passo era
    // inalcançável por link direto.
    //
    // A checagem usa o id da URL, não `resolvedProposalId`: este vem do
    // contexto, que só é populado por outro efeito, então no primeiro render
    // ainda está vazio — e era exatamente nesse render que o redirect
    // disparava.
    if (proposalIdInUrl && !proposal) return;
    if (activeIndex === -1) {
      goToStep(steps[0].code, true);
      return;
    }
    if (activeCode === stepCode) return;
    goToStep(activeCode, true);
  }, [
    steps,
    activeIndex,
    activeCode,
    stepCode,
    goToStep,
    proposalIdInUrl,
    proposal,
  ]);

  const handleNext = (result?: RdQuoteStepOnNextResult) => {
    if (!steps.length) return;

    const proposalIdArg = result?.proposalId?.trim();

    if (proposalIdArg && !hasNavigatedToId.current) {
      // Caminho de criação (Step 1 → próximo): `resolvedProposalId` ainda está
      // vazio neste render, então `productType` caiu para 'individual' e
      // `steps` reflete o fluxo individual. Readaptamos a lista com o
      // `productType` retornado pela criação para escolher o próximo step certo
      // (agregado → FLEET; individual → EQUIPMENT, inalterado).
      const adapted = adaptStepsForFlow(
        RD_WIZARD_STEPS,
        result?.productType ?? productType
      );
      const nextCode =
        adapted[adapted.findIndex(s => s.code === activeCode) + 1]?.code;
      if (!nextCode) return;

      setParams({ policyId: proposalIdArg, id: proposalIdArg });
      hasNavigatedToId.current = true;
      navigate({
        to: '/quotes/products/new/$id',
        params: { id: proposalIdArg },
        search: { ...baseSearch, stepCode: nextCode },
        replace: true,
        viewTransition: false,
      }).catch(() => {});
      return;
    }

    const next = steps[activeIndex + 1];
    if (!next) return;
    goToStep(next.code, false);
  };

  const handleBack = () => {
    const prev = steps[activeIndex - 1];
    if (!prev) return;
    goToStep(prev.code, false);
  };

  // Proposta inexistente/inacessível (ex.: GET 404): só se aplica ao editar
  // uma proposta existente — nunca ao fluxo de nova proposta (sem id) nem
  // durante o carregamento inicial (`isProposalLoading`), quando o
  // skeleton/spinner de cada step já cobre o estado transitório.
  if (resolvedProposalId && isProposalError && !isProposalLoading) {
    return (
      <div className='flex flex-col gap-6'>
        <Alert.Root status='error'>
          <Alert.Icon />
          <Alert.Content>
            <Alert.Title>Proposta não encontrada</Alert.Title>
            <Alert.Description>
              Não foi possível carregar esta proposta. Ela pode ter sido
              removida ou o link utilizado está incorreto.
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
        <div>
          <Link to='/proposals'>
            <Button
              variant='secondary'
              size='md'
              className='flex items-center gap-2'
            >
              <ArrowLeft className='h-5 w-5' strokeWidth={1.6} />
              Voltar para propostas RD
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Modo leitura vem de DUAS fontes: a intenção declarada na URL (`view`) e o
  // estado real da proposta. Derivar só da URL deixava uma proposta emitida
  // totalmente editável sempre que ela fosse aberta por um link com
  // `quoteFlow=new|edit` — que é como a lista de Propostas a abre.
  const isViewMode =
    quoteFlow === 'view' || isRdProposalFrozen(proposal?.status);
  const ActiveStep = STEP_REGISTRY[activeCode] ?? StepCliente;
  const canGoBack = activeIndex > 0;

  return (
    <div className='flex flex-col gap-8'>
      <div className='md:hidden'>
        <div className='mb-3 text-sm font-medium text-alba-gray-600'>
          Etapa {safeIndex + 1} de {steps.length || 0}
        </div>
        <div className='overflow-hidden rounded-2xl bg-white px-3 py-5 shadow-md'>
          <div
            className='grid gap-1'
            style={{
              gridTemplateColumns: `repeat(${steps.length || 1}, minmax(0, 1fr))`,
            }}
          >
            {steps.map((step, index) => {
              const isActive = index === safeIndex;
              const isCompleted = index < safeIndex;

              return (
                <div
                  key={step.code}
                  className='flex min-w-0 flex-col items-center gap-2'
                >
                  <div
                    className={[
                      'flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold',
                      isActive
                        ? 'bg-alba-blue-500 text-white'
                        : isCompleted
                          ? 'bg-alba-green-500 text-white'
                          : 'bg-alba-gray-200 text-alba-gray-600',
                    ].join(' ')}
                  >
                    {index + 1}
                  </div>
                  <span
                    className={[
                      'line-clamp-2 w-full wrap-break-word text-center text-[10px] leading-tight',
                      isActive
                        ? 'text-alba-black-500 font-medium'
                        : 'text-alba-gray-600',
                    ].join(' ')}
                    title={step.stepName}
                  >
                    {step.stepName}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className='hidden md:block'>
        <Steps.Root step={safeIndex} count={steps.length} linear>
          <Steps.List>
            {steps.map((step, index) => (
              <Steps.Item key={step.code} index={index}>
                <Steps.Trigger className='pointer-events-none cursor-default'>
                  <Steps.Indicator />
                  <Steps.Title>{step.stepName}</Steps.Title>
                </Steps.Trigger>
                {index < steps.length - 1 && <Steps.Separator />}
              </Steps.Item>
            ))}
          </Steps.List>
        </Steps.Root>
      </div>

      <div className='relative isolate flex w-full flex-col gap-8 rounded-2xl bg-white p-4 shadow-md sm:p-8'>
        <ActiveStep
          onNext={handleNext}
          onBack={handleBack}
          canGoBack={canGoBack}
          isViewMode={isViewMode}
        />
      </div>
    </div>
  );
}
